/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, objectSpread2 as _objectSpread2, defineProperty as _defineProperty } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useCallback, useEffect } from 'react';
import debounce from '../../../../Utilities/debounce/debounce.js';

var _excluded = ["render", "rows", "headers", "filterAllLabel", "initialFilters", "filterItems", "filterKeys", "onExternalFilterChange", "onExternalFilterRows"];

/* eslint-disable react/prop-types */

/*
 * Higher Order Component that wraps DataTable,
 * returning a new component with filters support
 */
var computeFilters = function computeFilters(input) {
  var current = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (input && input.columnKey) {
    var selected = [];
    var selectedItem = input.selectedItem,
      selectedItems = input.selectedItems,
      value = input.value;
    if (input.isDateRange) {
      selected = selectedItems; // array with 2 date objects for start date and end date
    } else if (Object.prototype.hasOwnProperty.call(input, 'selectedItem')) {
      if (selectedItem) {
        selected = [selectedItem].map(function (item) {
          return item.id;
        });
      }
    } else if (Object.prototype.hasOwnProperty.call(input, 'selectedItems')) {
      selected = selectedItems.map(function (item) {
        return item.id;
      });
    } else if (Object.prototype.hasOwnProperty.call(input, 'value')) {
      selected = value;
    }
    var key = input.columnKey;
    return _objectSpread2(_objectSpread2({}, current), {}, _defineProperty({}, key, selected));
  }
  return current;
};

/*
 * transform filters for DropDown, ComboBox, and MultiSelect
 * no transformation happens for TextInput
 *
 * filterItems: {
 *    attached_groups: [
 *      { id: 'g1', label: 'Kevins VM Groups' },
 *      { id: 'g2', label: 'Maureens VM Groups' },
 *    ]
 * }
 * input:
 *   {
 *      attached_groups: ['g1'],
 *      status...
 *   }
 * output:
 *   {
 *      attached_groups: [{ id: 'g1', label: 'Kevins VM Groups' }],
 *      status...
 *   }
 * Note: if no filterItems provided, value of id will be used for label
 */
var transform = function transform(input, filterItems) {
  var output = {};
  Object.keys(input).forEach(function (key) {
    if (typeof input[key] === 'string') {
      output[key] = input[key];
    } else {
      output[key] = input[key].map(function (item, i) {
        if (item instanceof Date) {
          var start = item;
          var end = input[key][i + 1];
          if (end instanceof Date) {
            return {
              id: "".concat(start.getTime(), "-").concat(end.getTime()),
              label: "".concat(start.toLocaleDateString(), " - ").concat(end.toLocaleDateString()),
              start: start,
              end: end
            };
          }
          return null;
        }
        if (filterItems && Array.isArray(filterItems[key])) {
          var filterItem = filterItems[key].find(function (entry) {
            return entry.id === item;
          });
          if (filterItem) {
            var _filterItem$label;
            return {
              id: filterItem.id,
              label: (_filterItem$label = filterItem.label) !== null && _filterItem$label !== void 0 ? _filterItem$label : filterItem.id
            };
          }
        }
        return {
          id: item,
          label: item
        };
      }).filter(function (e) {
        return e;
      });
    }
  });
  return output;
};
var trimTime = function trimTime(input) {
  var output = new Date(input);
  output.setHours(0, 0, 0, 0);
  return output;
};
var filterRows = function filterRows(_ref) {
  var rows = _ref.rows,
    filterKeys = _ref.filterKeys,
    filters = _ref.filters;
  if (filters && Object.keys(filters).length > 0) {
    return Object.keys(filters).reduce(function (results, key) {
      var selections = filters[key];
      return results.filter(function (candidate) {
        /*
         * filter by key (display value) by default;
         * if filterKey is provided, filter by filterKey (e.g. an id) instead
         */
        var value = filterKeys && filterKeys[key] ? candidate[filterKeys[key]] : candidate[key];

        // date range filtering, trimming the time portion to eliminate errors caused by timezone differences
        if (Array.isArray(selections) && selections[0] instanceof Date && selections[1] instanceof Date) {
          var start = trimTime(selections[0]);
          var end = trimTime(selections[1]);
          var valObj = trimTime(value);
          return valObj.getTime() >= start.getTime() && valObj.getTime() <= end.getTime();
        }
        if (selections && typeof selections === 'string') {
          return selections === value;
        }

        // no selections (all) or a match
        return selections.length === 0 || selections.includes(value);
      });
    }, rows);
  }
  return rows;
};
var withFilters = function withFilters(WrappedDataTable) {
  return function (_ref2) {
    var render = _ref2.render,
      rows = _ref2.rows,
      headers = _ref2.headers,
      filterAllLabel = _ref2.filterAllLabel,
      initialFilters = _ref2.initialFilters,
      filterItems = _ref2.filterItems,
      filterKeys = _ref2.filterKeys,
      onExternalFilterChange = _ref2.onExternalFilterChange,
      onExternalFilterRows = _ref2.onExternalFilterRows,
      passthroughProps = _objectWithoutProperties(_ref2, _excluded);
    var _useState = useState(initialFilters),
      _useState2 = _slicedToArray(_useState, 2),
      prevInitialFilters = _useState2[0],
      setPrevInitialFilters = _useState2[1];
    var _useState3 = useState(initialFilters || {}),
      _useState4 = _slicedToArray(_useState3, 2),
      filters = _useState4[0],
      setFilters = _useState4[1];
    var _useState5 = useState(initialFilters || {}),
      _useState6 = _slicedToArray(_useState5, 2),
      batchFilters = _useState6[0],
      setBatchFilters = _useState6[1];
    var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      isFilterPanelOpen = _useState8[0],
      setFilterPanelOpen = _useState8[1];
    var _useState9 = useState(!onExternalFilterChange && rows ? filterRows({
        rows: rows,
        filterKeys: filterKeys,
        filters: filters
      }) : undefined),
      _useState10 = _slicedToArray(_useState9, 2),
      filteredRows = _useState10[0],
      setFilteredRows = _useState10[1];
    var changeFilters = useCallback(function (tempFilters) {
      setFilters(tempFilters);
      if (onExternalFilterChange) debounce(onExternalFilterChange(tempFilters), 300);
    }, [onExternalFilterChange]);

    // Reset the applied filters when initialFilters changes
    if (initialFilters !== prevInitialFilters) {
      changeFilters(initialFilters || {});
      setPrevInitialFilters(initialFilters);
    }

    // If an external filter function is provided then do not filter the rows
    // here. Otherwise do the internal filtering when the rows or filters change.
    useEffect(function () {
      if (!onExternalFilterChange && rows) {
        setFilteredRows(filterRows({
          rows: rows,
          filterKeys: filterKeys,
          filters: filters
        }));
      }
    }, [rows, filterKeys, filters, onExternalFilterChange]);

    // Trigger callback whenever the filters change
    useEffect(function () {
      if (onExternalFilterRows) onExternalFilterRows();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);
    var handleFilterChange = function handleFilterChange(value) {
      if (value.mode === 'batch') {
        var newBatchFilters = computeFilters(value, batchFilters);
        setBatchFilters(newBatchFilters);
      } else {
        var newFilters = computeFilters(value, filters);
        changeFilters(newFilters);
      }
    };
    var handleFilterClear = function handleFilterClear(preservedFilterKeys) {
      var clearedFilters = Object.fromEntries(Object.entries(filters).filter(function (entry) {
        return preservedFilterKeys.includes(entry[0]);
      }));
      changeFilters(clearedFilters);
      setBatchFilters(clearedFilters);
    };
    var handleCommitFilters = function handleCommitFilters() {
      changeFilters(batchFilters);
    };
    var augmentedRender = function augmentedRender(renderProps) {
      var augmentedRenderProps = _objectSpread2(_objectSpread2({}, renderProps), {}, {
        headers: headers,
        filterItems: filterItems,
        filterSelections: transform(filters, filterItems) || {},
        isFilterPanelOpen: isFilterPanelOpen,
        onOpenFilterPanel: function onOpenFilterPanel() {
          return setFilterPanelOpen(!isFilterPanelOpen);
        },
        onCloseFilterPanel: function onCloseFilterPanel() {
          return setFilterPanelOpen(false);
        },
        onFilterChange: handleFilterChange,
        onClearAllFilters: handleFilterClear,
        onCommitFilters: handleCommitFilters,
        filterAllLabel: filterAllLabel
      });
      return render(augmentedRenderProps);
    };
    return /*#__PURE__*/React.createElement(WrappedDataTable
    // eslint-disable-next-line react/jsx-props-no-spreading
    , _extends({}, passthroughProps, {
      rows: filteredRows || rows,
      headers: headers,
      render: augmentedRender
    }));
  };
};
var withFilters$1 = withFilters;

export { computeFilters, withFilters$1 as default, transform };
